import clamp from "lodash/clamp";
import round from "lodash/round";
import { Unit } from "../../types/customization/box";
import { Cropping } from "../../types/customization/content";
import { SelectionPhoto } from "../../types/selection/selection";

export function calcBounds({
  cropping,
  croppingRatio,
  imageRatio,
}: {
  cropping: Cropping;
  croppingRatio: number;
  imageRatio: number;
}): {
  constrainedByWidth: boolean;
  referenceLength: number;
  referenceLengthMax: number;
  referenceLengthMin: number;
  scaleMax: number;
  scaleMin: number;
  xMax: number;
  xMin: number;
  yMax: number;
  yMin: number;
} {
  // console.log("cropping", round(cropping.x.value, 1), round(cropping.y.value, 1), round(cropping.width.value, 1), round(cropping.height.value, 1));

  const rotated = cropping.rotation % 180 === 90;

  const constrainedByWidth = !rotated ? imageRatio <= croppingRatio : imageRatio >= croppingRatio;
  // console.log("constrainedByWidth", constrainedByWidth);

  const referenceLength = constrainedByWidth ? cropping.width.value : cropping.height.value;
  const referenceLengthMin = !rotated
    ? 1000
    : imageRatio <= croppingRatio
    ? 1000 * imageRatio
    : 1000 / imageRatio;
  const referenceLengthMax = !rotated
    ? 10000
    : imageRatio <= croppingRatio
    ? 10000 * imageRatio
    : 10000 / imageRatio;

  // console.log("referenceLength", referenceLength, referenceLengthMin, referenceLengthMax);

  const xMin = round(rotated ? 5000 - 10000 / imageRatio / 2 : 0);
  const xMax = round((rotated ? 5000 + 10000 / imageRatio / 2 : 10000) - cropping.width.value);
  const yMin = round(rotated ? 5000 - (10000 * imageRatio) / 2 : 0);
  const yMax = round((rotated ? 5000 + (10000 * imageRatio) / 2 : 10000) - cropping.height.value);
  // console.log("x bounds", minX, maxX);
  // console.log("y bounds", minY, maxY);

  return {
    constrainedByWidth,
    referenceLength,
    referenceLengthMax,
    referenceLengthMin,
    scaleMax: referenceLength / referenceLengthMin,
    scaleMin: referenceLength / referenceLengthMax,
    xMax,
    xMin,
    yMax,
    yMin,
  };
}

export function calcDefaultCropping({
  selectionPhoto,
  nextRatio,
}: {
  selectionPhoto: SelectionPhoto;
  nextRatio: number;
}): Cropping {
  const prevRatio = selectionPhoto.width.value / selectionPhoto.height.value;

  const prevCropping: Cropping = {
    default: true,
    height: { value: 10000, unit: Unit.P10K },
    rotation: 0,
    width: { value: 10000, unit: Unit.P10K },
    x: { value: 0, unit: Unit.P10K },
    y: { value: 0, unit: Unit.P10K },
  };

  return calcNextCropping({
    nextRatio,
    prevCropping,
    prevRatio,
  });
}

export function calcNextCropping({
  prevCropping,
  prevRatio,
  nextRatio,
}: {
  prevCropping: Cropping;
  prevRatio: number;
  nextRatio: number;
}): Cropping {
  const nextCropping: Cropping = {
    default: prevCropping.default,
    height: { ...prevCropping.height },
    rotation: prevCropping.rotation,
    width: { ...prevCropping.width },
    x: { ...prevCropping.x },
    y: { ...prevCropping.y },
  };

  if (ratioEquals(nextRatio, prevRatio)) {
    return nextCropping;
  }

  const imageRatio = (prevRatio * prevCropping.height.value) / prevCropping.width.value;
  // console.log("imageRatio", imageRatio);

  /*
   * We do calculation based on an image of 100,000 px
   */
  const imageW = 100000;
  const imageH = imageW / imageRatio;
  // console.log("imageSize", imageW, imageH);

  const prevX = (prevCropping.x.value * imageW) / 10000;
  const prevY = (prevCropping.y.value * imageH) / 10000;
  const prevW = (prevCropping.width.value * imageW) / 10000;
  const prevH = (prevCropping.height.value * imageH) / 10000;
  // console.log("prevCropping", prevX, prevY, prevW, prevH);

  const toCropping = (x: number, y: number, w: number, h: number): Cropping => {
    // console.log("cropping", x, y, w, h);

    return {
      default: prevCropping.default,
      height: { value: round((10000 * h) / imageH), unit: Unit.P10K },
      rotation: prevCropping.rotation,
      width: { value: round((10000 * w) / imageW), unit: Unit.P10K },
      x: { value: round((10000 * x) / imageW), unit: Unit.P10K },
      y: { value: round((10000 * imageRatio * y) / imageW), unit: Unit.P10K },
    };
  };

  const rotated = prevCropping.rotation % 180 === 90;

  const leftBound = rotated ? (imageW - imageH) / 2 : 0;
  const rightBound = leftBound + (rotated ? imageH : imageW);
  const topBound = rotated ? (imageH - imageW) / 2 : 0;
  const bottomBound = topBound + (rotated ? imageW : imageH);

  let nextH;
  let nextW;
  if (nextRatio < prevRatio && !rotated) {
    nextW = prevW;
    nextH = nextW / nextRatio;
    if (nextH > bottomBound - leftBound) {
      nextW = (bottomBound - leftBound) * nextRatio;
      nextH = bottomBound - leftBound;
    }
  } else {
    nextH = prevH;
    nextW = nextH * nextRatio;
    if (nextW > rightBound - leftBound) {
      nextH = (rightBound - leftBound) / nextRatio;
      nextW = rightBound - leftBound;
    }
  }

  const nextX = clamp(prevX + (prevW - nextW) / 2, leftBound, rightBound - nextW);
  const nextY = clamp(prevY + (prevH - nextH) / 2, topBound, bottomBound - nextH);

  return toCropping(nextX, nextY, nextW, nextH);
}

function ratioEquals(ratioA: number, ratioB: number): boolean {
  return Math.abs(ratioA - ratioB) < 0.005;
}
